import {
  type ComponentProps,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { Box } from "../../../mads";
import { useThemedStylesheet } from "../../../mads/ThemeProvider";
import { createThemedStylesheet } from "../../../shared/util/createThemedStylesheet";
import { ModalTemplate } from "../ModalTemplate";
import { PseudoInput } from "./components/PseudoInput";

/** Props passed to modal content render function */
type ComboboxModalContentProps<ValueT> = {
  /** Current draft value (may differ from committed value) */
  value: ValueT;
  /** Update the draft value */
  onChange: (value: ValueT) => void;
};

export type ComboboxModalProps<ValueT> = {
  /** Modal animation type */
  animationType?: ComponentProps<typeof ModalTemplate>["animationType"];
  /**
   * Open the picker once on mount, skipping the initial pseudo-input tap. For
   * hosts that reveal the combobox as a direct user action (e.g. the Add-skill
   * sheet). Reopening after a close stays a manual tap. Defaults to false.
   */
  autoOpen?: boolean;
  /** Compare committed and draft values. Returns true if they differ. Defaults to reference equality. */
  isDirtyFn?: (committed: ValueT, draft: ValueT) => boolean;
  /** Render function that receives draft value and setter */
  modalContent: (props: ComboboxModalContentProps<ValueT>) => ReactNode;
  /** Title displayed in the modal header */
  modalTitle: ComponentProps<typeof ModalTemplate>["title"];
  /** Called when user clicks "Done" to commit the draft value */
  onChange: (value: ValueT) => void;
  /** Called immediately when modal dismissal begins. */
  onCloseStart?: () => void;
  /** Called after the modal finishes presenting. */
  onShow?: () => void;
  /** Render function for the trigger input that opens the modal */
  pseudoInput: (args: { handleOpenPopup: () => void }) => ReactNode;
  /**
   * Called when the modal opens (`true`) or closes (`false`). Fires
   * synchronously on the trigger press, before the modal's keyboard shows, so a
   * host can react to the modal being open (e.g. suppress a keyboard-driven
   * layout change that would otherwise collapse this open modal). Defaults to
   * none.
   */
  onOpenChange?: (isOpen: boolean) => void;
  /** Test ID for testing purposes */
  testID?: string;
  /** Committed value controlled by parent */
  value: ValueT;
  /** Modal sizing variant: 'fill' (pageSheet, default), 'fit' (bottom sheet), or 'fullScreen' */
  variant?: ComponentProps<typeof ModalTemplate>["variant"];
};

export function ComboboxModal<ValueT>({
  animationType,
  autoOpen,
  isDirtyFn,
  modalContent,
  modalTitle,
  onChange,
  onCloseStart,
  onOpenChange,
  onShow,
  pseudoInput,
  testID,
  value,
  variant,
}: ComboboxModalProps<ValueT>) {
  const styles = useThemedStylesheet(stylesheet);

  // The picker is a ModalTemplate mounted inside the host's own ModalTemplate-based
  // sheet, so *when* autoOpen opens it matters:
  //  - 'fit' (Android) animates in via its own internal effect, so seeding the
  //    open state at mount is safe.
  //  - 'fill' / 'fullScreen' drive the native modal's `visible` directly; opening
  //    it in the same commit that mounts it (mid content-swap) makes iOS drop the
  //    presentation, so flip it open one commit later once the mount settles.
  const isFitVariant = variant === "fit";
  const [isPopupOpen, setIsPopupOpen] = useState(!!autoOpen && isFitVariant);
  const [draftValue, setDraftValue] = useState<ValueT>(value);

  const didAutoOpenRef = useRef(false);
  useEffect(() => {
    if (!autoOpen || didAutoOpenRef.current) return;
    didAutoOpenRef.current = true;
    // 'fit' seeds isPopupOpen at mount (see above); only 'fill' / 'fullScreen'
    // flip it here. Either way the modal is now open, so notify hosts exactly
    // once — matching the onOpenChange(true) a manual handleOpenPopup fires.
    if (!isFitVariant) setIsPopupOpen(true);
    onOpenChange?.(true);
    // Empty deps: run once on mount so a later value/handler change can't
    // cancel or re-trigger the deferred open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenPopup = useCallback(() => {
    setDraftValue(value);
    setIsPopupOpen(true);
    onOpenChange?.(true);
  }, [value, onOpenChange]);

  const handleClosePopup = useCallback(() => {
    setIsPopupOpen(false);
    onOpenChange?.(false);
  }, [onOpenChange]);

  const handleDone = useCallback(() => {
    onChange(draftValue);
    setIsPopupOpen(false);
    onOpenChange?.(false);
  }, [draftValue, onChange, onOpenChange]);

  const isDirty = isDirtyFn ? isDirtyFn(value, draftValue) : value !== draftValue;

  return (
    <>
      <Box sx={styles.inputContainer} id={`${testID}-input-container`}>
        {pseudoInput({ handleOpenPopup })}
      </Box>
      <ModalTemplate
        animationType={animationType}
        isDirty={isDirty}
        onClose={handleClosePopup}
        onCloseStart={onCloseStart}
        onDone={handleDone}
        onShow={onShow}
        title={modalTitle}
        variant={variant}
        visible={isPopupOpen}
      >
        {modalContent({ value: draftValue, onChange: setDraftValue })}
      </ModalTemplate>
    </>
  );
}

ComboboxModal.PseudoInput = PseudoInput;

const stylesheet = createThemedStylesheet((_theme) => ({
  inputContainer: {
    width: "100%",
  },
}));
